export const PRODUCTION_COLUMNS = [
  { key: "estrategia", label: "Estratégia", etapa: "planejamento", title: "Reunião de estratégia de conteúdo" },
  { key: "roteiro", label: "Roteiro", etapa: "pre_producao", title: "Criar roteiros dos conteúdos" },
  { key: "alinhamento", label: "Alinhamento de roteiro", etapa: "pre_producao", title: "Alinhamento de roteiro" },
  { key: "captacao", label: "Captação", etapa: "captacao", title: "Captação" },
  { key: "edicao", label: "Edição", etapa: "pos_producao", title: "Edição dos conteúdos" },
  { key: "aprovacao", label: "Cliente aprova", etapa: "aprovacao", title: "Enviar conteúdos para aprovação" },
  { key: "programacao", label: "Programa", etapa: "programacao", title: "Programar postagens" },
  { key: "resultado", label: "Resultado", etapa: "publicacao", title: "Reunião de resultados" },
  { key: "outros", label: "Outras atividades", etapa: "planejamento", title: "" },
];

export const PLANNING_CONDITIONS = [
  { key: "", label: "Data definida" },
  { key: "a_agendar", label: "A agendar" },
  { key: "apos_aprovacao", label: "Após aprovação" },
  { key: "apos_publicacao", label: "Após publicação" },
  { key: "proposta", label: "Data proposta" },
];

export const UNDATED_CONDITIONS = new Set(["a_agendar", "apos_aprovacao", "apos_publicacao"]);
export const PROGRAMMING_SCOPE = [
  { key: "", label: "Pelo estúdio" },
  { key: "pelo_cliente", label: "Pelo cliente" },
  { key: "fora_escopo", label: "Fora do nosso escopo" },
];
const COLUMN_KEYS = new Set(PRODUCTION_COLUMNS.map((c) => c.key));
const normalize = (value = "") => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const isMonthly = (cycle) => ["mensal", "recorrente", "bimestral"].includes(cycle.tipo);
const aliveCycle = (cycle) => !cycle.arquivado && cycle.status !== "cancelado" && !cycle.cliente_encerrado_em;

// Older tasks have no board column. Use specific titles first; retain unknown work.
export function taskColumn(task) {
  if (COLUMN_KEYS.has(task.planejamento_coluna)) return task.planejamento_coluna;
  const name = normalize(task.titulo);
  if (/resultado|relatorio|metricas/.test(name)) return "resultado";
  if (/alinhamento.*roteiro|roteiro.*alinhamento/.test(name)) return "alinhamento";
  if (/roteiro/.test(name)) return "roteiro";
  if (/estrategia/.test(name)) return "estrategia";
  if (/captacao|gravacao|filmagem/.test(name)) return "captacao";
  if (/edicao|editar|pos.producao/.test(name)) return "edicao";
  if (/programar|programacao|agendar.*post|agendar.*publica|acompanhar.*publica/.test(name)) return "programacao";
  if (/aprovacao|aprovar/.test(name)) return "aprovacao";
  return ({ captacao: "captacao", pos_producao: "edicao", aprovacao: "aprovacao", programacao: "programacao" })[task.etapa] || "outros";
}

export function monthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function monthLabel(value) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value || "")) return value || "Sem mês";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

export function availableMonths(cycles, today = new Date()) {
  return [...new Set([monthKey(today), monthKey(new Date(today.getFullYear(), today.getMonth() + 1, 1)), ...cycles.map((c) => c.competencia)])]
    .filter((value) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value || "")).sort();
}

export function taskScheduleLabel(task) {
  const condition = PLANNING_CONDITIONS.find((item) => item.key === task.planejamento_condicao)?.label;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(task.prazo || "") ? `${task.prazo.slice(8, 10)}/${task.prazo.slice(5, 7)}` : "";
  if (task.planejamento_condicao === "proposta") return date ? `${date} · proposta` : "Data proposta a definir";
  // A concrete date supplied by the cycle's editing sequence takes precedence.
  if (date) return date;
  if (task.planejamento_condicao && condition) return condition;
  return date || "A agendar";
}

export function rowDeliverables(row) {
  const configured = row.cycles.map((c) => c.entregaveis_planejados?.trim()).filter(Boolean);
  if (configured.length) return [...new Set(configured)].join(" · ");
  if (row.kind === "project") return row.cycles[0]?.total_entregaveis ? `${row.cycles[0].total_entregaveis} entregáveis` : "Não informado";
  if (row.client.videos_mes) return `${row.client.videos_mes} vídeo${row.client.videos_mes === 1 ? "" : "s"}/mês`;
  return row.client.entregas_mes ? `${row.client.entregas_mes} entregas/mês` : "Não informado";
}

export function buildProductionRows(clients, cycles, tasks, month) {
  const clientsById = new Map(clients.filter((c) => !c.deleted_at && c.status !== "encerrado").map((c) => [c.id, c]));
  const eligible = cycles.filter((c) => aliveCycle(c) && clientsById.has(c.cliente_id));
  const monthly = eligible.filter((c) => isMonthly(c) && c.competencia === month);
  const makeRow = (client, rowCycles, kind, key) => {
    const ids = new Set(rowCycles.map((c) => c.id));
    const rowTasks = tasks.filter((t) => ids.has(t.ciclo_id) && t.status !== "cancelado" && !t.cliente_encerrado_em);
    const done = rowTasks.filter((t) => t.status === "concluido").length;
    return { key, client, cycles: rowCycles, kind, tasks: rowTasks, done, progress: rowTasks.length ? Math.round(done / rowTasks.length * 100) : 0 };
  };
  const recurring = [...clientsById.values()]
    .filter((c) => c.tipo === "recorrente" || monthly.some((cycle) => cycle.cliente_id === c.id))
    .sort((a, b) => a.empresa.localeCompare(b.empresa, "pt-BR"))
    .map((c) => makeRow(c, monthly.filter((cycle) => cycle.cliente_id === c.id), "recurring", `client:${c.id}`));
  const projects = eligible.filter((c) => !isMonthly(c) && (c.status !== "concluido" || c.competencia === month))
    .sort((a, b) => clientsById.get(a.cliente_id).empresa.localeCompare(clientsById.get(b.cliente_id).empresa, "pt-BR") || a.titulo.localeCompare(b.titulo))
    .map((c) => makeRow(clientsById.get(c.cliente_id), [c], "project", `project:${c.id}`));
  return { recurring, projects };
}
